import useCreateUpload from '@/api/mutations/file/useCreateUpload';
import useDeleteProposalAttachment from '@/api/mutations/proposals/useDeleteProposalAttachment';
import useRenameFile from '@/api/mutations/file/useRenameFile';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import {
  TMarketingAttachment,
  TProposalAttachment,
  useGetProposalAttachments,
} from '@/api/queries/proposal/useGetProposal';
import { findTypeAndIconFromMimeType } from '@/helpers/fileHelper';
import ConfirmDeleteModal from '@/ui/components/confirm-delete-modal/ConfirmDeleteModal';
import RenameAttachmentModal from '@/ui/pages/proposalBuilderPage/components/RenameAttachmentModal';
import { useHashFragment } from '@/utils/customHooks';
import { Button, TDropdownItem, useToastContext, Text } from '@payaca/ambrosia';
import FolderEntry from '@payaca/components/folderEntry/FolderEntry';
import Lightbox, {
  TProps as TLightboxProps,
} from '@payaca/components/lightbox/Lightbox';
import {
  FileSizeLimit,
  FileUploadType,
  getAcceptedFileTypes,
  getMbFileLimit,
} from '@payaca/helpers/fileHelper';
import UntitledIcon from '@payaca/untitled-icons';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useDropzone } from 'react-dropzone';

const ACCEPTED_FILE_TYPES = getAcceptedFileTypes([
  'document',
  'image',
  'video',
  'other',
]);

const MAX_FILE_SIZE_BYTES = FileSizeLimit[FileUploadType.JOB_ATTACHMENT];

const RENAME_MODAL_HASH = '#rename-attachment';

const getLightboxDetail = (
  attachment?: Pick<TProposalAttachment, 'fileName' | 'mimeType' | 'url'> & {
    thumbnailUrl?: string | null;
  }
): TLightboxProps['lightboxDetail'] => {
  if (!attachment) return { type: 'loading' };

  const { mimeType, url, thumbnailUrl, fileName } = attachment;

  if (mimeType.startsWith('video/')) {
    return {
      type: 'video',
      videoType: mimeType,
      src: url,
      title: fileName,
      subtitle: null,
    };
  }
  if (mimeType === 'application/pdf') {
    return { type: 'pdf', src: url, title: fileName, subtitle: null };
  }
  if (mimeType.startsWith('image/')) {
    return {
      type: 'image',
      src: url,
      thumbnailSrc: thumbnailUrl ?? undefined,
      title: fileName,
      subtitle: null,
    };
  }
  // Files we can't render inline (documents etc.) — offer to open in a new tab.
  return {
    type: 'link',
    label: 'Open file',
    href: url,
    title: fileName,
    subtitle: null,
  };
};

export type TProps = {
  proposalId: string;
};

const AttachmentsTab: FC<TProps> = (props) => {
  const { proposalId } = props;

  const queryClient = useQueryClient();
  const { pushError } = useToastContext();
  const { attachments, marketingAttachments } =
    useGetProposalAttachments(proposalId);

  const entries: (
    | { kind: 'proposal'; attachment: TProposalAttachment }
    | { kind: 'marketing'; attachment: TMarketingAttachment }
  )[] = [
    ...(attachments ?? []).map((attachment) => ({
      kind: 'proposal' as const,
      attachment,
    })),
    ...(marketingAttachments ?? []).map((attachment) => ({
      kind: 'marketing' as const,
      attachment,
    })),
  ];

  const [attachmentToRename, setAttachmentToRename] =
    useState<TProposalAttachment>();
  const [isRenameModalOpen, toggleRenameModal] =
    useHashFragment(RENAME_MODAL_HASH);
  const [attachmentToDelete, setAttachmentToDelete] =
    useState<TProposalAttachment>();
  const [isUploading, setIsUploading] = useState(false);
  const [viewingAttachmentId, setViewingAttachmentId] = useState<string>();

  const viewingIndex = entries.findIndex(
    ({ attachment }) => attachment.id === viewingAttachmentId
  );
  const isViewing = viewingIndex >= 0;
  const nextAttachmentId =
    isViewing && viewingIndex < entries.length - 1
      ? entries[viewingIndex + 1].attachment.id
      : undefined;
  const prevAttachmentId =
    isViewing && viewingIndex > 0
      ? entries[viewingIndex - 1].attachment.id
      : undefined;

  const invalidateAttachments = () =>
    queryClient.invalidateQueries({
      queryKey: proposalKeys.proposal(proposalId),
    });

  const { mutateAsync: createUpload } = useCreateUpload();

  const { mutate: deleteAttachment, isPending: isDeleting } =
    useDeleteProposalAttachment({
      proposalId,
      options: {
        onSuccess: () => setAttachmentToDelete(undefined),
      },
    });

  const { mutate: renameFile, isPending: isRenaming } = useRenameFile({
    onSuccess: () => {
      void invalidateAttachments();
      toggleRenameModal();
    },
  });

  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return;

    setIsUploading(true);
    const results = await Promise.allSettled(
      files.map((file) =>
        createUpload({
          file,
          linkedEntities: [
            {
              entityId: proposalId,
              entityType: 'job',
              uploadRole: 'attachment',
            },
          ],
        })
      )
    );
    setIsUploading(false);

    // Refresh the list if any upload succeeded. Failures already surface via
    // the global mutation error toast.
    if (results.some((result) => result.status === 'fulfilled')) {
      void invalidateAttachments();
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    // The whole tab is the drop target, but clicking it must not open the
    // picker — that is the dedicated button's job.
    noClick: true,
    noKeyboard: true,
    multiple: true,
    accept: ACCEPTED_FILE_TYPES.join(','),
    maxSize: MAX_FILE_SIZE_BYTES,
    onDrop: (acceptedFiles) => void handleUpload(acceptedFiles),
    onDropRejected: (rejections) => {
      const isTooLarge = rejections.some((rejection) =>
        rejection.errors.some((error) => error.code === 'file-too-large')
      );
      pushError(
        isTooLarge
          ? `File is too large. The maximum size is ${getMbFileLimit(MAX_FILE_SIZE_BYTES)}.`
          : 'That file type is not supported.'
      );
    },
  });

  const contextMenuItems = (
    attachment: TProposalAttachment
  ): TDropdownItem[] => [
    {
      label: 'Rename',
      onClick: (e) => {
        e.stopPropagation();
        setAttachmentToRename(attachment);
        toggleRenameModal();
      },
    },
    {
      label: 'Delete',
      onClick: (e) => {
        e.stopPropagation();
        setAttachmentToDelete(attachment);
      },
    },
  ];

  return (
    <div
      {...getRootProps({ 'aria-label': 'Drop files here to upload' })}
      className="relative flex flex-col gap-2.5 px-3.5 h-full"
    >
      <input {...getInputProps()} />

      {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2.5">
        {entries.map(({ attachment, kind }) => {
          const [fileType, thumbnailIconName] = findTypeAndIconFromMimeType(
            attachment.mimeType
          );

          return (
            <FolderEntry
              key={attachment.id}
              fileName={attachment.fileName}
              fileType={fileType}
              thumbnailIconName={thumbnailIconName}
              thumbnailUrl={
                kind === 'proposal'
                  ? attachment.thumbnailUrl ?? undefined
                  : undefined
              }
              contextMenuItems={
                kind === 'proposal' ? contextMenuItems(attachment) : undefined
              }
              badge={
                kind === 'marketing'
                  ? { text: 'Company', variant: 'soft' }
                  : undefined
              }
              checkbox={false}
              onClick={() => setViewingAttachmentId(attachment.id)}
            />
          );
        })}
      </div>

      <Button
        variant="tertiary"
        className="w-full"
        onClick={open}
        isProcessing={isUploading}
        disabled={isUploading}
      >
        <UntitledIcon name="upload-01.3" className="h-4 w-4" />
        Upload attachment
      </Button>

      {isDragActive && (
        <div className="pointer-events-none absolute inset-0 left-2.5 right-2.5 border-2 border-dashed z-10 flex items-center justify-center rounded-lg bg-blue-50/80">
          <Text>Drop files to upload</Text>
        </div>
      )}

      <Lightbox
        isOpen={isViewing}
        onClose={() => setViewingAttachmentId(undefined)}
        onNext={
          nextAttachmentId
            ? () => setViewingAttachmentId(nextAttachmentId)
            : undefined
        }
        onPrev={
          prevAttachmentId
            ? () => setViewingAttachmentId(prevAttachmentId)
            : undefined
        }
        lightboxDetail={getLightboxDetail(
          isViewing ? entries[viewingIndex]?.attachment : undefined
        )}
      />

      <RenameAttachmentModal
        attachmentToRename={attachmentToRename}
        isOpen={isRenameModalOpen}
        isProcessing={isRenaming}
        onSave={(fileName) => {
          if (attachmentToRename) {
            renameFile({ id: attachmentToRename.id, name: fileName });
          }
        }}
        onClose={toggleRenameModal}
      />

      <ConfirmDeleteModal
        isOpen={!!attachmentToDelete}
        onClose={() => setAttachmentToDelete(undefined)}
        onConfirmDelete={() => {
          if (attachmentToDelete) {
            deleteAttachment({
              proposalId,
              uploadId: attachmentToDelete.id,
            });
          }
        }}
        isBusy={isDeleting}
        entityName={{ singular: 'attachment', plural: 'attachments' }}
      />
    </div>
  );
};

export default AttachmentsTab;
